/**
 * "Scrivi a piu' persone": letture e scritture su Supabase.
 *
 * Tutto passa dalla RLS dell'organizzazione; in piu' le fasi della pipeline
 * rispettano le restrizioni di visibilita' del ruolo e il "vedi solo i tuoi"
 * (filter_by_user), come la lista dei Lead. Nessun messaggio parte da qui: si
 * scrivono righe programmate che il core spedisce (queue/scheduledMessages.ts).
 */
import { supabase } from '../../../supabase';
import { applyLeadVisibilityToQuery, type LeadVisibility } from '../../../utils/leadVisibility';
import type { BulkCandidate, KnownChats } from './bulkRecipients';
import type { BulkChannel } from './bulkSchedule';
import type { BulkReportRow } from './bulkReport';

const CLIENT_COLS = 'id, business_name, contact_name, entity_type, phone, whatsapp_number, ai_processing_opt_out_source';
const CHUNK = 100;

interface ClientRow {
  id: string;
  business_name: string | null;
  contact_name: string | null;
  entity_type: string | null;
  phone: string | null;
  whatsapp_number: string | null;
  ai_processing_opt_out_source: string | null;
}

const toCandidate = (c: ClientRow): BulkCandidate => ({
  clientId: c.id,
  businessName: c.business_name,
  contactName: c.contact_name,
  entityType: c.entity_type,
  phone: c.phone,
  whatsappNumber: c.whatsapp_number,
  optOut: c.ai_processing_opt_out_source === 'stop_keyword',
});

function chunks<T>(arr: T[], size = CHUNK): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/** Lead/Clienti scelti a mano (selezione nella lista Lead). */
export async function loadClientCandidates(orgId: string, clientIds: string[]): Promise<BulkCandidate[]> {
  const out: BulkCandidate[] = [];
  for (const ids of chunks([...new Set(clientIds)])) {
    const { data, error } = await supabase
      .from('clients').select(CLIENT_COLS)
      .eq('organization_id', orgId).is('deleted_at', null).in('id', ids);
    if (error) throw error;
    out.push(...((data || []) as ClientRow[]).map(toCandidate));
  }
  return out;
}

/** Lead in una fase della pipeline, con la stessa visibilita' della lista Lead. */
export async function loadStageCandidates(
  orgId: string,
  pipelineId: string,
  stageId: string,
  opts: { visibility: LeadVisibility | null; onlyAssignedTo?: string | null },
): Promise<BulkCandidate[]> {
  let q = supabase
    .from('clients').select(CLIENT_COLS)
    .eq('organization_id', orgId).is('deleted_at', null)
    .eq('pipeline_id', pipelineId).eq('pipeline_stage_id', stageId);
  q = applyLeadVisibilityToQuery(q, opts.visibility);
  if (opts.onlyAssignedTo) q = q.eq('assigned_to', opts.onlyAssignedTo);
  const { data, error } = await q.limit(1000);
  if (error) throw error;
  return ((data || []) as ClientRow[]).map(toCandidate);
}

/** Chat con un'etichetta; se il numero e' in anagrafica si usa la scheda (nome, STOP). */
export async function loadLabelCandidates(orgId: string, labelId: string): Promise<BulkCandidate[]> {
  const { data: assigned, error } = await supabase
    .from('whatsapp_chat_label_assignments').select('chat_jid')
    .eq('organization_id', orgId).eq('label_id', labelId).limit(1000);
  if (error) throw error;
  const jids = [...new Set((assigned || []).map((a: { chat_jid: string }) => a.chat_jid))];
  if (jids.length === 0) return [];

  const chatInfo = new Map<string, { digits: string; name: string | null }>();
  for (const part of chunks(jids)) {
    const { data } = await supabase
      .from('whatsapp_chats').select('chat_jid, phone_e164, display_name')
      .eq('organization_id', orgId).in('chat_jid', part);
    for (const c of (data || []) as Array<{ chat_jid: string; phone_e164: string | null; display_name: string | null }>) {
      const digits = (c.phone_e164 || '').replace(/\D/g, '');
      if (digits && !chatInfo.has(c.chat_jid)) chatInfo.set(c.chat_jid, { digits, name: c.display_name });
    }
  }

  const byDigits = await clientsByDigits(orgId, [...chatInfo.values()].map((c) => c.digits));
  return jids.map((jid): BulkCandidate => {
    const info = chatInfo.get(jid);
    const digits = info?.digits || (jid.endsWith('@c.us') ? jid.split('@')[0].replace(/\D/g, '') : '');
    const client = digits ? byDigits.get(digits) : undefined;
    if (client) return { ...toCandidate(client), chatJid: digits ? `${digits}@c.us` : jid, chatName: info?.name };
    return { clientId: null, chatJid: digits ? `${digits}@c.us` : jid, chatName: info?.name ?? null };
  });
}

/** Anagrafiche per numero (phone_digits o whatsapp_number), come i nomi delle chat. */
async function clientsByDigits(orgId: string, digits: string[]): Promise<Map<string, ClientRow>> {
  const variantTo = new Map<string, string>();
  for (const d of digits) {
    variantTo.set(d, d);
    if (d.startsWith('39') && d.length > 10) variantTo.set(d.slice(2), d);
  }
  const out = new Map<string, ClientRow>();
  for (const part of chunks([...variantTo.keys()])) {
    const list = part.join(',');
    const { data } = await supabase
      .from('clients').select(`${CLIENT_COLS}, phone_digits`)
      .eq('organization_id', orgId).is('deleted_at', null)
      .or(`phone_digits.in.(${list}),whatsapp_number.in.(${list})`).limit(1000);
    for (const c of (data || []) as Array<ClientRow & { phone_digits: string | null }>) {
      for (const v of [c.phone_digits, (c.whatsapp_number || '').replace(/\D/g, '')]) {
        const d = v ? variantTo.get(v) : undefined;
        if (d && !out.has(d)) out.set(d, c);
      }
    }
  }
  return out;
}

/** Numeri che hanno gia' una chat: nell'organizzazione e sul numero da cui si invia. */
export async function loadKnownChats(orgId: string, configId: string, digits: string[]): Promise<KnownChats> {
  const known: KnownChats = { inOrg: new Set(), onConfig: new Set() };
  for (const part of chunks([...new Set(digits)].filter(Boolean))) {
    const { data, error } = await supabase
      .from('whatsapp_chats').select('config_id, chat_key')
      .eq('organization_id', orgId).in('chat_key', part.map((d) => `p:${d}`));
    if (error) throw error;
    for (const c of (data || []) as Array<{ config_id: string; chat_key: string }>) {
      const d = c.chat_key.slice(2);
      known.inOrg.add(d);
      if (c.config_id === configId) known.onConfig.add(d);
    }
  }
  return known;
}

export interface ApprovedTemplate { id: string; name: string; language: string; category: string; components: unknown[] }

export async function loadApprovedTemplates(configId: string): Promise<ApprovedTemplate[]> {
  const { data, error } = await supabase
    .from('whatsapp_templates').select('id, name, language, category, components')
    .eq('config_id', configId).eq('status', 'APPROVED').order('updated_at', { ascending: false });
  if (error) throw error;
  return (data || []) as ApprovedTemplate[];
}

export interface BulkSendHeader {
  organization_id: string;
  config_id: string;
  created_by: string;
  channel: BulkChannel;
  message_text: string | null;
  template_name: string | null;
  template_language: string | null;
  audience_label: string;
  total_recipients: number;
  excluded_count: number;
  excluded_summary: Record<string, number>;
  first_send_at: string;
  last_send_at: string;
}

/**
 * Crea l'invio e le righe programmate. Se le righe non si salvano tutte, quelle
 * gia' salvate vengono annullate: meglio nessun messaggio che meta' invio.
 */
export async function createBulkSend(header: BulkSendHeader, buildRows: (bulkSendId: string) => Record<string, unknown>[]): Promise<string> {
  const { data, error } = await supabase.from('whatsapp_bulk_sends').insert(header).select('id').single();
  if (error) throw error;
  const id = (data as { id: string }).id;
  try {
    for (const part of chunks(buildRows(id))) {
      const { error: insErr } = await supabase.from('whatsapp_scheduled_messages').insert(part);
      if (insErr) throw insErr;
    }
  } catch (err) {
    await cancelRemaining(id);
    throw err;
  }
  return id;
}

/** Annulla i messaggi dell'invio non ancora partiti. */
export async function cancelRemaining(bulkSendId: string): Promise<void> {
  const { error } = await supabase
    .from('whatsapp_scheduled_messages').update({ status: 'cancelled' })
    .eq('bulk_send_id', bulkSendId).eq('status', 'scheduled');
  if (error) throw error;
}

export interface BulkSendListItem {
  id: string;
  channel: BulkChannel;
  config_id: string | null;
  message_text: string | null;
  template_name: string | null;
  audience_label: string | null;
  total_recipients: number;
  excluded_count: number;
  created_at: string;
  created_by: string | null;
}

export async function listBulkSends(orgId: string, limit = 30): Promise<BulkSendListItem[]> {
  const { data, error } = await supabase
    .from('whatsapp_bulk_sends')
    .select('id, channel, config_id, message_text, template_name, audience_label, total_recipients, excluded_count, created_at, created_by')
    .eq('organization_id', orgId).order('created_at', { ascending: false }).limit(limit);
  if (error) throw error;
  return (data || []) as BulkSendListItem[];
}

export async function fetchBulkReport(bulkSendId: string): Promise<BulkReportRow[]> {
  const { data, error } = await supabase.rpc('whatsapp_bulk_send_report', { p_bulk_send_id: bulkSendId });
  if (error) throw error;
  return (data || []) as BulkReportRow[];
}
